import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, type Credential } from '../api';
import { externalApi, type ExternalResource, type FinanceSummary, type FinancialKind, type FinancialObservationRow, type SourceKind } from '../api/external';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import './Finance.css';

const KINDS: SourceKind[] = ['stripe', 'paypal', 'woocommerce'];
/** Brand names, not translations. */
const KIND_LABEL: Record<SourceKind, string> = { stripe: 'Stripe', paypal: 'PayPal', woocommerce: 'WooCommerce' };
/** Data series use neutral tokens only; a dedicated chart palette is requested from the tokens owner (Issue). */
const SERIES = ['var(--c-accent)', 'var(--c-text-muted)', 'var(--c-ok)', 'var(--c-border)'];
/** Business timezones bucket months on the server; the query window is widened by a day each side so boundary hours are not cut off by the browser's local month. */
const TZ_MARGIN_MS = 24 * 3600 * 1000;
const PERIODS = [3, 6, 12] as const;
const PAGE_SIZE = 50;
const CHART_HEIGHT = 160;

const fmtDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' }) : '—');
function fmtMoney(amount: number | string, currency: string): string {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  try {
    return new Intl.NumberFormat('uk-UA', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}
const monthStart = (ym: string) => new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1);
const ymOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthShort = (ym: string) => monthStart(ym).toLocaleString('uk-UA', { month: 'short' }).replace(/\.$/, '');
const monthLong = (ym: string) => monthStart(ym).toLocaleString('uk-UA', { month: 'long' });

/**
 * Last N calendar months up to the current one: `from` inclusive, `to` exclusive (first day of next
 * month) for the operations table; `summaryFrom` / `summaryTo` widened for the summary query, whose
 * rows are then picked by month name.
 */
function periodOf(months: number): { from: string; to: string; summaryFrom: string; summaryTo: string; months: string[] } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const list: string[] = [];
  for (let i = 0; i < months; i++) list.push(ymOf(new Date(first.getFullYear(), first.getMonth() + i, 1)));
  return {
    from: first.toISOString(),
    to: next.toISOString(),
    summaryFrom: new Date(first.getTime() - TZ_MARGIN_MS).toISOString(),
    summaryTo: new Date(next.getTime() + TZ_MARGIN_MS).toISOString(),
    months: list,
  };
}

/** Sums per currency, rendered as "120,00 € · 49,00 $"; each amount stays on one line, wrapping happens only between currencies (390px). */
function perCurrency(entries: Array<{ currency: string; value: number }>, fallbackCurrency: string) {
  const sums = new Map<string, number>();
  for (const e of entries) sums.set(e.currency, (sums.get(e.currency) ?? 0) + e.value);
  const parts = sums.size === 0 ? [[fallbackCurrency, 0] as const] : [...sums.entries()];
  return parts.map(([c, v], i) => (
    <span key={c} style={{ whiteSpace: 'nowrap' }}>
      {i > 0 ? ' · ' : ''}
      {fmtMoney(v, c)}
    </span>
  ));
}

export function Finance({ canRead, canReadSources, canManage }: { canRead: boolean; canReadSources: boolean; canManage: boolean }) {
  const [businessId, setBusinessId] = useState('');
  const [months, setMonths] = useState<(typeof PERIODS)[number]>(6);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const period = useMemo(() => periodOf(months), [months]);

  useEffect(() => {
    if (!canRead) return;
    externalApi
      .summary(period.summaryFrom, period.summaryTo, businessId || undefined)
      .then((r) => setSummary({ ...r, rows: r.rows.filter((row) => period.months.includes(row.month)) }))
      .catch((e: Error) => setError(e.message));
  }, [canRead, period, businessId]);

  if (!canRead) {
    return (
      <div className="finance">
        <h1>{t('finance.title')}</h1>
        <div className="card muted">{t('finance.noAccess')}</div>
      </div>
    );
  }

  const businesses = summary?.businesses ?? [];
  const selected = businesses.find((b) => b.id === businessId);
  const fallbackCurrency = selected?.currency ?? businesses[0]?.currency ?? 'EUR';
  const latest = period.months[period.months.length - 1]!;
  const latestRows = (summary?.rows ?? []).filter((r) => r.month === latest);

  return (
    <div className="finance">
      <div className="head">
        <h1>{t('finance.title')}</h1>
        <div className="row">
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('finance.sources.form.business')}>
            <option value="">{t('finance.filter.allBusinesses')}</option>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value) as (typeof PERIODS)[number])} aria-label={t('finance.filter.period')}>
            {PERIODS.map((m) => (
              <option key={m} value={m}>{t(m === 3 ? 'finance.filter.months3' : m === 6 ? 'finance.filter.months6' : 'finance.filter.months12')}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="muted">{t('finance.hint')}</p>
      {error && <div className="error">{error}</div>}

      <div className="kpi">
        <div>
          <span className="muted">{t('finance.kpi.revenue', { month: monthLong(latest) })}</span>
          <strong>{perCurrency(latestRows.map((r) => ({ currency: r.currency, value: r.revenue })), fallbackCurrency)}</strong>
        </div>
        <div>
          <span className="muted">{t('finance.kpi.refunds')}</span>
          <strong>{perCurrency(latestRows.map((r) => ({ currency: r.currency, value: -r.refunds })), fallbackCurrency)}</strong>
        </div>
        <div>
          <span className="muted">{t('finance.kpi.fees')}</span>
          <strong>{perCurrency(latestRows.map((r) => ({ currency: r.currency, value: -r.fees })), fallbackCurrency)}</strong>
        </div>
        <div>
          <span className="muted">{t('finance.kpi.aiCost')}</span>
          {/* Real cost of the agent runs that finished in this month (phase 2), per currency of the run. */}
          <strong>{perCurrency(latestRows.map((r) => ({ currency: r.currency, value: r.aiCost })), fallbackCurrency)}</strong>
        </div>
      </div>

      <Chart summary={summary} months={period.months} />

      {canReadSources && <Sources businessId={businessId} businesses={businesses} canManage={canManage} />}

      <Operations businessId={businessId} from={period.from} to={period.to} />
    </div>
  );
}

function Chart({ summary, months }: { summary: FinanceSummary | null; months: string[] }) {
  const businesses = summary?.businesses ?? [];
  const rows = summary?.rows ?? [];
  // Revenue in each business's own currency; other currencies are listed in the table, not stacked here.
  const mixed = rows.some((r) => r.currency !== businesses.find((b) => b.id === r.businessId)?.currency);
  const byMonth = months.map((m) => businesses.map((b) => rows.filter((r) => r.month === m && r.businessId === b.id && r.currency === b.currency).reduce((s, r) => s + r.revenue, 0)));
  const max = Math.max(0, ...byMonth.map((vals) => vals.reduce((a, v) => a + v, 0)));
  const cols = { gridTemplateColumns: `repeat(${months.length}, 1fr)` };

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>{t('finance.chart.title')}</strong>
        <div className="legend">
          {businesses.map((b, i) => (
            <span key={b.id}>
              <i style={{ background: SERIES[i % SERIES.length] }} />
              {b.name}
            </span>
          ))}
        </div>
      </div>
      {max === 0 && <div className="muted" style={{ marginTop: 'var(--sp-2)' }}>{t('finance.chart.empty')}</div>}
      <div className="bars" style={cols} role="img" aria-label={t('finance.chart.title')}>
        {byMonth.map((vals, mi) => (
          <div className="bar" key={months[mi]}>
            {vals.map((v, bi) => (
              <i key={businesses[bi]!.id} style={{ height: max > 0 ? `${(v / max) * CHART_HEIGHT}px` : 0, background: SERIES[bi % SERIES.length] }} title={`${businesses[bi]!.name}: ${fmtMoney(v, businesses[bi]!.currency)}`} />
            ))}
          </div>
        ))}
      </div>
      <div className="months" style={cols}>
        {months.map((m) => (
          <span key={m}>{monthShort(m)}</span>
        ))}
      </div>
      {mixed && <div className="muted" style={{ marginTop: 'var(--sp-2)' }}>{t('finance.chart.mixedCurrency')}</div>}
    </div>
  );
}

function stateBadge(r: ExternalResource, queued: boolean) {
  if (queued) return <span className="badge">{t('finance.sources.queued')}</span>;
  if (!r.enabled) return <span className="badge">{t('finance.sources.state.disabled')}</span>;
  if (!r.state) return <span className="badge">{t('finance.sources.state.new')}</span>;
  if (r.state.failureCount > 0) {
    return (
      <span className="badge danger" title={r.state.lastError ?? undefined}>
        {t('finance.sources.state.error', { n: r.state.failureCount })}
        {r.state.nextRetryAt ? ` · ${t('finance.sources.state.retry', { at: fmtDateTime(r.state.nextRetryAt) })}` : ''}
      </span>
    );
  }
  // No failures but a retry time set: the last run stopped with more to read and continues on the next tick.
  if (r.state.nextRetryAt) return <span className="badge">{t('finance.sources.state.partial', { n: r.observations })}</span>;
  if (!r.state.lastSuccessAt) return <span className="badge">{t('finance.sources.state.new')}</span>;
  return <span className="badge ok">{t('finance.sources.state.ok', { n: r.observations })}</span>;
}

function Sources({ businessId, businesses, canManage }: { businessId: string; businesses: FinanceSummary['businesses']; canManage: boolean }) {
  const [rows, setRows] = useState<ExternalResource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);

  const reload = () =>
    externalApi
      .resources(businessId || undefined)
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const sync = async (id: string) => {
    setError(null);
    setQueued((q) => ({ ...q, [id]: true }));
    try {
      await externalApi.syncResource(id);
      // The worker reports back through SyncState; give it a moment, then re-read.
      setTimeout(() => {
        void reload().finally(() => setQueued((q) => ({ ...q, [id]: false })));
      }, 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
      setQueued((q) => ({ ...q, [id]: false }));
    }
  };

  const toggle = async (r: ExternalResource) => {
    setError(null);
    await externalApi.updateResource(r.id, { enabled: !r.enabled }).catch((e: Error) => setError(e.message));
    await reload();
  };

  const columns: Column<ExternalResource>[] = [
    { key: 'source', title: t('finance.sources.col.source'), render: (r) => `${r.kind ? KIND_LABEL[r.kind] : r.kind} · ${r.name}`, sortValue: (r) => `${r.kind} ${r.name}` },
    { key: 'business', title: t('finance.sources.col.business'), render: (r) => r.businessName, sortValue: (r) => r.businessName },
    {
      key: 'key',
      title: t('finance.sources.col.key'),
      render: (r) => (r.credential ? `${r.credential.provider} / ${r.credential.name}${r.credential.revoked ? ` · ${t('team.keys.revoked')}` : ''}` : '—'),
      wrap: true,
    },
    { key: 'last', title: t('finance.sources.col.lastSync'), render: (r) => (r.state?.lastSuccessAt ? fmtDateTime(r.state.lastSuccessAt) : t('finance.sources.never')), sortValue: (r) => r.state?.lastSuccessAt ?? null },
    { key: 'state', title: t('finance.sources.col.state'), render: (r) => stateBadge(r, queued[r.id] === true), sortValue: (r) => (r.state?.failureCount ?? 0) },
    ...(canManage
      ? [
          {
            key: 'actions',
            title: '',
            render: (r: ExternalResource) => (
              <span className="actions">
                <button type="button" className="btn secondary small" onClick={() => sync(r.id)} disabled={queued[r.id] === true || !r.enabled || !r.credential || r.credential.revoked}>
                  {t('finance.sources.sync')}
                </button>
                <button type="button" className="btn secondary small" onClick={() => toggle(r)}>{t(r.enabled ? 'finance.sources.disable' : 'finance.sources.enable')}</button>
              </span>
            ),
          } satisfies Column<ExternalResource>,
        ]
      : []),
  ];

  return (
    <>
      <h2>{t('finance.sources')}</h2>
      {error && <div className="error">{error}</div>}
      <Table columns={columns} rows={rows} rowKey={(r) => r.id} empty={t('common.none')} />
      {canManage && !adding && (
        <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
          <button type="button" className="btn secondary small" onClick={() => setAdding(true)}>{t('finance.sources.add')}</button>
        </div>
      )}
      {canManage && adding && (
        <SourceForm
          businesses={businesses}
          defaultBusinessId={businessId}
          onDone={() => {
            setAdding(false);
            void reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </>
  );
}

function SourceForm({ businesses, defaultBusinessId, onDone, onCancel }: { businesses: FinanceSummary['businesses']; defaultBusinessId: string; onDone: () => void; onCancel: () => void }) {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [business, setBusiness] = useState(defaultBusinessId || businesses[0]?.id || '');
  const [kind, setKind] = useState<SourceKind>('stripe');
  const [name, setName] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [sandbox, setSandbox] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.credentials().then(setCredentials).catch((e: Error) => setError(e.message));
  }, []);

  const usable = credentials.filter((c) => c.provider === kind && !c.revokedAt);
  useEffect(() => {
    if (!usable.some((c) => c.id === credentialId)) setCredentialId(usable[0]?.id ?? '');
  }, [kind, credentials]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const config: Record<string, unknown> = {};
      if (kind === 'woocommerce') config.baseUrl = baseUrl;
      if (kind === 'paypal' && sandbox) config.sandbox = true;
      if (startDate) config.startDate = startDate;
      await externalApi.createResource({ businessId: business, kind, name, credentialId, config });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form card" onSubmit={submit}>
      <strong>{t('finance.sources.form.title')}</strong>
      <label>
        {t('finance.sources.form.business')}
        <select value={business} onChange={(e) => setBusiness(e.target.value)} required>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </label>
      <label>
        {t('finance.sources.form.kind')}
        <select value={kind} onChange={(e) => setKind(e.target.value as SourceKind)}>
          {KINDS.map((k) => (
            <option key={k} value={k}>{KIND_LABEL[k]}</option>
          ))}
        </select>
      </label>
      <label>
        {t('finance.sources.form.name')}
        <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
      </label>
      <label>
        {t('finance.sources.form.credential')}
        {usable.length > 0 ? (
          <select value={credentialId} onChange={(e) => setCredentialId(e.target.value)} required>
            {usable.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        ) : (
          <span className="muted">{t('finance.sources.form.noCredential')}</span>
        )}
      </label>
      <div className="muted">{t(kind === 'stripe' ? 'finance.sources.form.secretHint.stripe' : kind === 'paypal' ? 'finance.sources.form.secretHint.paypal' : 'finance.sources.form.secretHint.woocommerce')}</div>
      {kind === 'woocommerce' && (
        <label>
          {t('finance.sources.form.baseUrl')}
          <input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} required placeholder="https://" />
        </label>
      )}
      {kind === 'paypal' && (
        <div className="checks">
          <label>
            <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} />
            {t('finance.sources.form.sandbox')}
          </label>
        </div>
      )}
      <label>
        {t('finance.sources.form.startDate')}
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </label>
      {error && <div className="error">{error}</div>}
      <div className="row">
        <button className="btn" type="submit" disabled={busy || !credentialId}>{t('finance.sources.form.submit')}</button>
        <button className="btn secondary" type="button" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}

function Operations({ businessId, from, to }: { businessId: string; from: string; to: string }) {
  const [rows, setRows] = useState<FinancialObservationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<FinancialKind | ''>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [businessId, from, to, kind]);

  useEffect(() => {
    externalApi
      .observations({ businessId: businessId || undefined, from, to, kind: kind || undefined, page, pageSize: PAGE_SIZE })
      .then((r) => {
        setTotal(r.total);
        setRows((prev) => (page === 1 ? r.rows : [...prev, ...r.rows]));
      })
      .catch((e: Error) => setError(e.message));
  }, [businessId, from, to, kind, page]);

  const signed = (r: FinancialObservationRow) => (r.kind === 'payment' ? Number(r.amount) : -Number(r.amount));
  const kindLabel = (k: FinancialKind) => t(k === 'payment' ? 'finance.ops.kind.payment' : k === 'refund' ? 'finance.ops.kind.refund' : 'finance.ops.kind.fee');

  const columns: Column<FinancialObservationRow>[] = [
    { key: 'when', title: t('finance.ops.col.when'), render: (r) => fmtDateTime(r.occurredAt), sortValue: (r) => r.occurredAt },
    { key: 'business', title: t('finance.ops.col.business'), render: (r) => r.businessName, sortValue: (r) => r.businessName },
    { key: 'source', title: t('finance.ops.col.source'), render: (r) => KIND_LABEL[r.source] ?? r.source, sortValue: (r) => r.source },
    { key: 'kind', title: t('finance.ops.col.kind'), render: (r) => kindLabel(r.kind), sortValue: (r) => r.kind },
    { key: 'amount', title: t('finance.ops.col.amount'), render: (r) => <span className="num">{fmtMoney(signed(r), r.currency)}</span>, sortValue: (r) => signed(r) },
    { key: 'ext', title: t('finance.ops.col.externalId'), render: (r) => <span className="muted mono">{r.externalId}</span>, sortValue: (r) => r.externalId },
  ];

  return (
    <>
      <h2>{t('finance.ops')}</h2>
      <div className="filters">
        <select value={kind} onChange={(e) => setKind(e.target.value as FinancialKind | '')} aria-label={t('finance.ops.col.kind')}>
          <option value="">{t('finance.ops.filter.allKinds')}</option>
          <option value="payment">{t('finance.ops.kind.payment')}</option>
          <option value="refund">{t('finance.ops.kind.refund')}</option>
          <option value="fee">{t('finance.ops.kind.fee')}</option>
        </select>
        <span className="muted">{t('finance.ops.total', { n: total })}</span>
      </div>
      {error && <div className="error">{error}</div>}
      <Table columns={columns} rows={rows} rowKey={(r) => r.id} empty={t('common.none')} />
      {rows.length < total && (
        <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
          <button type="button" className="btn secondary small" onClick={() => setPage((p) => p + 1)}>{t('finance.ops.more')}</button>
        </div>
      )}
    </>
  );
}
